import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, EmptyState, Heading, Input, Row, Text } from '@plurall/elo-beta'

import {
  EMAIL_VALIDATOR,
  graphqlClient,
  hasRole,
  inputValidateCompleteName,
  mobileRemoveMask,
  ROLE_STUDENT_ID,
} from 'utils'
import { ROUTES } from 'utils/constants'
import { ADD_RESPONSIBLE, CHECK_EMAIL_INVITE_RESPONSIBLE, CHECK_MOBILE } from 'graphql/mutations'
import { useDebounce } from 'hooks'
import UserContext from 'stores/UserStore'

import styles from './AccountApproval.module.css'

const AccountApproval = () => {
  const userStore = useContext(UserContext)
  const person = { ...userStore.person }
  const [loading, setLoading] = useState(false)
  const [errorMobile, setErrorMobile] = useState(null)
  const [errorEmail, setErrorEmail] = useState(null)
  const [errorName, setErrorName] = useState(null)
  const [name, setName] = useState(null)
  const [email, setEmail] = useState(null)
  const [mobile, setMobile] = useState('')
  const [responsibleAdded, setResponsibleAdded] = useState(null)
  const hasParents = person?.parents?.length
  const isStudent = hasRole(person, ROLE_STUDENT_ID)
  const navigate = useNavigate()

  const debounceEmail = useDebounce(emailField => {
    setEmail(emailField)
    setErrorEmail(null)
  }, 300)

  const handleChangeEmail = emailField => {
    const validEmail = emailField.match(EMAIL_VALIDATOR)

    if (!validEmail) {
      setErrorEmail('E-mail inválido')
    } else {
      debounceEmail(emailField)
    }
  }

  const handleChangeName = nameField => {
    const { sanitizeCompleteName, error } = inputValidateCompleteName(nameField)

    if (error) {
      setErrorName(error)
    }

    setName(sanitizeCompleteName)
    setErrorName(null)
  }

  const handleChangeMobile = mobileField => {
    const mobileWithoutMask = `${mobileRemoveMask(mobileField)}`

    if (mobileField.length > 1 && mobileWithoutMask.length < 11) {
      setErrorMobile('Celular inválido')
    } else if (mobileWithoutMask.length === 11) {
      checkMobile(mobileField)
    } else if (mobileField.length === 0) {
      setErrorMobile(null)
    }
  }

  const handleSubmit = async () => {
    setLoading(true)

    const emailError = await checkEmail(email)

    const { error: nameError } = inputValidateCompleteName(name)

    if (emailError || nameError) {
      setLoading(false)

      return
    }

    const { errors } = await graphqlClient().mutate({
      errorPolicy: 'all',
      mutation: ADD_RESPONSIBLE,
      variables: {
        email,
        mobile: mobile ? `${mobileRemoveMask(mobile)}` : '',
        name,
      },
    })

    if (errors) {
      const errorMessage = errors.map(error => error.message).join(' ')

      window.PLURALL_ELO_TOAST_CONTAINER.addToast({
        variant: 'negative',
        text: errorMessage,
      })

      setLoading(false)

      return
    }

    setResponsibleAdded(true)

    setLoading(false)
  }

  const checkMobile = async mobileField => {
    const { errors } = await graphqlClient().mutate({
      errorPolicy: 'all',
      mutation: CHECK_MOBILE,
      variables: { mobile: mobileRemoveMask(mobileField) },
    })

    if (errors) {
      const errorMessage = errors.map(error => error.message).join(' ')

      setErrorMobile(errorMessage)
      return
    }

    setErrorMobile(null)
    setMobile(mobileField)
  }

  const checkEmail = async emailField => {
    const { errors } = await graphqlClient().mutate({
      errorPolicy: 'all',
      mutation: CHECK_EMAIL_INVITE_RESPONSIBLE,
      variables: {
        email: emailField,
      },
    })

    if (errors) {
      const errorMessage = errors.map(error => error.message).join(' ')

      setErrorEmail(errorMessage)
      return errorMessage
    }

    setEmail(emailField)
    setErrorEmail(null)
  }

  const renderConclusion = () => {
    return (
      <EmptyState title={'Responsável vinculado'} variation={'waving'}>
        <Text size='t3' dimmed alignment='center'>
          Para visualizar os responsáveis vinculados, clique em "Ver responsáveis vinculados".
        </Text>
        <Button
          onClick={() =>
            navigate(ROUTES.PERFIL_RESPONSAVEIS_VINCULADOS, { state: { responsibleAdded: true } })
          }
          minimal
          className={styles['button-success']}
        >
          Ver responsáveis vinculados
        </Button>
      </EmptyState>
    )
  }

  const renderForm = () => (
    <div className={styles['container']}>
      <div className={styles['grid']}>
        <div className={styles['row']}>
          <Heading element='h1' bold>
            Informe o nome completo e o e-mail do seu pai, mãe ou responsável
          </Heading>
        </div>

        <Row>
          {!hasParents && (
            <Text size='t2'>
              Como você é menor de idade, precisamos da aprovação de um responsável (pai, mãe ou
              responsável legal) para que você continue acessando o Plurall.
            </Text>
          )}
          <Text size='t2'>
            Para enviarmos uma solicitação de {hasParents ? 'vínculo' : 'aprovação'} da sua conta,
            informe o nome completo e o e-mail do seu responsável.
          </Text>
        </Row>

        <div>
          <div className={styles['row']}>
            <Input
              onChange={value => handleChangeName(value)}
              label='Nome completo do responsável'
              variant={errorName && 'negative'}
              disabled={loading}
              dataTestId='responsible-account-approval-name-input'
              validation={{
                validationText: errorName,
              }}
            />
          </div>

          <div className={styles['row']}>
            <Input
              onChange={value => handleChangeEmail(value)}
              label='E-mail do responsável'
              variant={errorEmail && 'negative'}
              disabled={loading}
              dataTestId='responsible-account-approval-email-input'
              validation={{
                validationText: errorEmail,
              }}
            />
          </div>
          <div className={styles['row']}>
            <Input
              type='cellphone'
              label='Celular'
              onChange={value => handleChangeMobile(value)}
              dataTestId='responsible-account-approval-mobile-input'
              variant={errorMobile && 'negative'}
              disabled={loading}
              validation={{
                validationText: errorMobile,
              }}
              hintText='Opcional'
            />
          </div>
        </div>
        <div className={styles['buttonRow']}>
          <Button
            dataTestId='email-btn-submit'
            disabled={errorEmail || !email || loading || errorMobile || !name || errorName}
            loading={loading}
            onClick={handleSubmit}
          >
            Solicitar aprovação
          </Button>
        </div>
      </div>
    </div>
  )

  if (!isStudent) {
    window.PLURALL_ELO_TOAST_CONTAINER.addToast({
      variant: 'negative',
      text: 'Área visível somente a alunos',
    })

    navigate(ROUTES.PERFIL_DADOS_PESSOAIS)
    return null
  }

  return responsibleAdded ? renderConclusion() : renderForm()
}

export default AccountApproval
